/**
 * Remote (Tailscale) access — the client half of backend/middleware/remoteAccess.js
 * (2026-09-02).
 *
 * Staff share one Google login. Reached over Tailscale
 * (https://warehouse.taile5c1c0.ts.net) the app is cut down to Local Deliveries
 * unless you are the admin or carry `remoteFullAccess`. The BACKEND is the
 * enforcement; this module exists so the phone lands straight on the delivery
 * board instead of flashing the Products grid and collecting 403s.
 *
 * Pure — no React, no axios — so it tests without a DOM.
 */

export const TAILNET_HOST_SUFFIX = '.ts.net';
export const REMOTE_RESTRICTED_CODE = 'REMOTE_RESTRICTED';

/** Is this browser talking to the app over the tailnet? */
export function isTailnetHost(hostname) {
  const host = String(hostname || '').trim().toLowerCase();
  if (!host) return false;
  return host.endsWith(TAILNET_HOST_SUFFIX);
}

/**
 * Should the UI limit itself to Local Deliveries?
 *
 * The server's own answer (`accessContext.remoteRestricted` from
 * GET /api/settings/my-permissions) wins whenever it is a boolean — it saw the
 * real Tailscale Serve headers, which the browser cannot. The host fallback
 * covers an older backend that does not send the field yet.
 *
 * Returns false while permissions are unknown: never lock anyone out before we
 * know who they are.
 */
export function computeRemoteRestricted({ permissions, accessContext, hostname } = {}) {
  if (typeof accessContext?.remoteRestricted === 'boolean') {
    return accessContext.remoteRestricted;
  }
  if (!permissions) return false;
  if (!isTailnetHost(hostname)) return false;
  if (permissions.isAdmin === true) return false;
  return permissions.remoteFullAccess !== true;
}

/**
 * Should the bare app root land on Local Deliveries because we came in over
 * the tailnet? (Rhys 2026-09-03: "im never picking offsite on my mobile".)
 *
 * This is the UNRESTRICTED case — the admin, or an account with the Remote
 * grant, whose root would otherwise auto-open picking on a phone or the
 * Products grid on a desktop. Only the root (`/`, `/products`, `/warehouse`)
 * is redirected; any other URL typed or bookmarked over Tailscale still
 * opens exactly what it names, so the full app stays one link away.
 * Restricted accounts never reach this: computeRemoteRestricted forces them
 * onto the delivery board for every path.
 */
export function tailnetRootLandsOnDeliveries({ hostname, isRootPath } = {}) {
  return isTailnetHost(hostname) && isRootPath === true;
}

/** Was this axios failure the tailnet guard rather than a real problem? */
export function isRemoteRestrictedError(error) {
  const response = error?.response;
  if (!response || response.status !== 403) return false;
  return response.data?.code === REMOTE_RESTRICTED_CODE;
}
